import { clsx } from 'clsx'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import {
  type AmountRoundingMode,
  formatTradeAmountToParts,
  getValueWithSuffixParts,
} from '@/utils/numberUtils'

type ColoredNumberProps = {
  value: number | string | undefined
  decimals?: number
  minimumDecimals?: number
  locale?: string
  currency?: string
  showSymbol?: boolean
  className?: string
  integerClassName?: string
  decimalClassName?: string
  prefix?: ReactNode
  suffix?: ReactNode
  isValueWithSuffix?: boolean
  roundingMode?: AmountRoundingMode
}

const resolveLocale = (locale?: string) => {
  if (locale) {
    return locale
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language
  }

  return new Intl.NumberFormat().resolvedOptions().locale
}

export const ColoredNumber = ({
  value,
  decimals,
  minimumDecimals,
  locale,
  currency,
  showSymbol,
  className,
  integerClassName,
  decimalClassName,
  prefix,
  suffix,
  isValueWithSuffix,
  roundingMode = 'round',
}: ColoredNumberProps) => {
  const resolvedDecimals = decimals ?? 2
  const resolvedMinimumDecimals = Math.min(minimumDecimals ?? resolvedDecimals, resolvedDecimals)
  const resolvedLocale = resolveLocale(locale)
  const resolvedCurrency = currency ?? 'USD'
  const resolvedShowSymbol = showSymbol ?? false

  const numericValue = Number(value)
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0

  const { parts, computedSuffix } = useMemo(() => {
    const mapParts = (formattedParts: Intl.NumberFormatPart[]) =>
      formattedParts.map((part) => ({
        value: part.value,
        isDecimal: part.type === 'decimal' || part.type === 'fraction',
      }))

    if (isValueWithSuffix) {
      const { value: scaledValue, fractionDigits, suffix } = getValueWithSuffixParts(safeValue)
      const roundedValue = Number(scaledValue.toFixed(fractionDigits))
      const formatter = new Intl.NumberFormat(resolvedLocale, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      })

      return {
        parts: mapParts(formatter.formatToParts(roundedValue)),
        computedSuffix: suffix,
      }
    }

    const formattedParts = formatTradeAmountToParts(safeValue, resolvedDecimals, {
      locale: resolvedLocale,
      currency: resolvedCurrency,
      showSymbol: resolvedShowSymbol,
      minimumDecimals: resolvedMinimumDecimals,
      roundingMode,
    })

    return {
      parts: mapParts(formattedParts),
      computedSuffix: '',
    }
  }, [
    isValueWithSuffix,
    resolvedLocale,
    resolvedCurrency,
    resolvedMinimumDecimals,
    resolvedDecimals,
    resolvedShowSymbol,
    roundingMode,
    safeValue,
  ])

  const resolvedIntegerClass = integerClassName ?? 'text-white'
  const decimalUsesDefault = !decimalClassName
  const resolvedDecimalClass = decimalClassName ?? resolvedIntegerClass

  const integerClasses = clsx(resolvedIntegerClass)
  const decimalClasses = isValueWithSuffix
    ? integerClasses
    : clsx(resolvedDecimalClass, decimalUsesDefault && 'opacity-50')
  const keyedParts = useMemo(() => {
    const counts = new Map<string, number>()
    return parts.map((part) => {
      const base = `${part.value}-${part.isDecimal ? 'decimal' : 'integer'}`
      const nextCount = (counts.get(base) ?? 0) + 1
      counts.set(base, nextCount)
      return { ...part, key: `${base}-${nextCount}` }
    })
  }, [parts])

  const combinedSuffix = suffix ?? computedSuffix

  return (
    <span className={clsx('inline-flex items-baseline', className)}>
      {prefix != null ? <span className={integerClasses}>{prefix}</span> : null}
      {keyedParts.length > 0 ? (
        keyedParts.map((part) => (
          <span key={part.key} className={part.isDecimal ? decimalClasses : integerClasses}>
            {part.value}
          </span>
        ))
      ) : (
        <span className={integerClasses}>0</span>
      )}
      {computedSuffix && suffix ? (
        <>
          <span className={integerClasses}>{computedSuffix}</span>
          <span className={integerClasses}>{suffix}</span>
        </>
      ) : combinedSuffix != null && combinedSuffix !== '' ? (
        <span className={integerClasses}>{combinedSuffix}</span>
      ) : null}
    </span>
  )
}

export default ColoredNumber
